// Edit-form section that maps a Directory Sync group to an admin role and permission (Owners only).
import { FC } from 'react';
import { FormikValues } from 'formik';
import { createUseStyles } from 'react-jss';
import DrawerFormSection from '@/components/form/drawer-section';
import FloatLabelCard from '@/components/float-label-card';
import InputControl from '@/components/form/input-control';
import Text from '@/components/text';
import InlineToast from '@/components/toasts/inline';
import useFeature from '@/hooks/use-feature.hook';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import {
  AdminPermissionLabels,
  CONVERSATION_ACCESS_PERMISSION,
} from '@/lib/models/admin-user.model';
import { GroupModel } from '@/lib/models/group.model';
import { GroupAdminRoleOptions } from './helpers';

interface Props {
  group: GroupModel;
  formHandle: FormHandle<FormikValues>;
}

const ROLE_MAPPING_WARNING =
  'Members of this group become Admin Users with this role. A member who already is an Admin User gets this role, even if it is lower than their current role.';
const SAVE_TIMING_HELP =
  'Saved changes apply within a few minutes. Changes in your identity provider apply on the next Directory Sync, every 30 minutes. A person who loses access can keep their current session for up to 60 minutes.';

const useStyles = createUseStyles({
  permissionsCard: {
    marginTop: 16,
    '& .MuiFormControl-root': {
      marginTop: 8,
      '& div:first-child': {
        justifyContent: 'space-between',
      },
    },
  },
});

const AdminAccessFields: FC<Props> = ({ group, formHandle }) => {
  const styles = useStyles();
  const { canManageAdmins } = useFeature();
  const canEditAdminAccess = group.isExternal && canManageAdmins;

  if (!canEditAdminAccess) {
    return null;
  }

  return (
    <>
      <DrawerFormSection label="Admin Roles and Permissions" />

      <InlineToast message={ROLE_MAPPING_WARNING} level="warning" />

      <InputControl
        name="adminRole"
        label="Admin Role"
        type="select"
        options={GroupAdminRoleOptions}
        formHandle={formHandle}
      />

      <FloatLabelCard label="Permissions" className={styles.permissionsCard}>
        <InputControl
          name="adminConversationAccess"
          label={AdminPermissionLabels[CONVERSATION_ACCESS_PERMISSION]}
          type="switch"
          formHandle={formHandle}
          size="small"
        />
      </FloatLabelCard>

      <Text size="small" color="grey">
        {SAVE_TIMING_HELP}
      </Text>
    </>
  );
};

export default AdminAccessFields;
